import Vue from 'vue';
import moment from 'moment-timezone';
import {
    directMessageScope, directMessageFilter, getSelectedDirectMessage, getDirectMessageIndex,
    getDirectMessageMentions, getAdjacentDirectMessage, isDirectMessage, DM_DATE_RANGE_DAYS
} from '@/conversation/utils/DirectMessageConversationProxy';
import {CONVERSATION_MENTION_LIMIT} from '@/conversation/utils/GrouseConversationProxy';
import {CONVERSATION_TOO_LARGE} from '@/conversation/utils/MentionConversationLoader';
import {compareMentionsByPublishedDate} from '@/conversation/utils/MentionConversationUtils';

const PAGE_SIZE = 20;
const internals = new WeakMap();
const cancelled = new Error('Conversation request cancelled');

/**
 * A bounded chronological index, with full mentions loaded only near the selection.
 * The index avoids timestamp-based paging gaps when several messages share a date.
 * Earlier/later rows first reveal indexed mentions, then explicitly extend the date range.
 * No synthetic reply links are added to mention data.
 */
export class DirectMessageConversationLoader {
    constructor(accountCode, selectedMention, timezone) {
        this.state = Vue.observable({loading: false, error: null, filter: '', rows: [], dateRange: null});
        internals.set(this, {accountCode, selectedMention, timezone, generation: 0, disposed: false,
            requests: new Set(), action: null, scope: null, index: [], mentionsById: new Map(), first: 0, end: 0,
            edges: {earlier: null, later: null}});
    }

    get loading() { return this.state.loading; }
    get error() { return this.state.error; }
    get filter() { return this.state.filter; }
    get dateRange() { return this.state.dateRange; }
    visibleRows() { return this.state.rows; }

    _current(generation) {
        const data = internals.get(this);
        return !data.disposed && generation === data.generation;
    }

    async _request(request, generation) {
        const data = internals.get(this);
        if (!this._current(generation)) {
            request.cancel?.();
            request.catch(() => {});
            throw cancelled;
        }
        data.requests.add(request);
        try {
            const result = await request;
            if (!this._current(generation)) throw cancelled;
            return result;
        } finally {
            data.requests.delete(request);
        }
    }

    async _index(filter, generation) {
        const data = internals.get(this);
        const mentions = await this._request(getDirectMessageIndex(data.accountCode, filter), generation);
        if (mentions.length > CONVERSATION_MENTION_LIMIT) this._tooLarge();
        const index = new Map(mentions.map(mention => [mention.id, mention]));
        // Keep the selection even if it was absent from the index response.
        const selected = data.mentionsById.get(data.selectedMention.id);
        index.set(selected.id, selected);
        if (index.size > CONVERSATION_MENTION_LIMIT) this._tooLarge();
        return [...index.values()].sort((a, b) => compareMentionsByPublishedDate(a, b) || a.id.localeCompare(b.id));
    }

    _tooLarge() {
        throw Object.assign(new Error(`This date range contains too many direct messages to display (maximum ${CONVERSATION_MENTION_LIMIT})`),
            {code: CONVERSATION_TOO_LARGE});
    }

    async _loadMentions(index, first, end, filter, generation) {
        const data = internals.get(this);
        const ids = index.slice(first, end).map(m => m.id).filter(id => !data.mentionsById.has(id));
        if (!ids.length) return;
        // A refreshed index can also discover newly ingested messages inside the displayed interval.
        // Load those too, retaining the same per-request limit as ordinary earlier/later pages.
        for (let offset = 0; offset < ids.length; offset += PAGE_SIZE) {
            const batch = ids.slice(offset, offset + PAGE_SIZE);
            const mentions = await this._request(getDirectMessageMentions(data.accountCode, filter, batch), generation);
            for (const mention of mentions) {
                if (batch.includes(mention.id) && isDirectMessage(mention)) data.mentionsById.set(mention.id, mention);
            }
        }
    }

    _edgeRow(kind, count) {
        if (count) return {id: `dm:${kind}`, kind, count};
        const data = internals.get(this);
        const edge = data.edges[kind];
        const boundary = this.dateRange[kind === 'earlier' ? 'start' : 'end'];
        // Initial checks stay invisible: a button-shaped placeholder would flash and disappear
        // when no messages exist. Show progress only after the reader explicitly retries a failure.
        if (!edge || edge.boundary !== boundary) return null;
        if (edge.loading) return edge.retry ? {id: `dm:${kind}`, kind, checking: true} : null;
        if (edge.error) return {id: `dm:${kind}`, kind, checkError: true};
        return edge.mention ? {id: `dm:${kind}`, kind, extendDateRange: true} : null;
    }

    async _checkEdge(kind, generation, retry = false) {
        const data = internals.get(this);
        const earlier = kind === 'earlier';
        const boundary = this.dateRange[earlier ? 'start' : 'end'];
        if (!retry && data.edges[kind]?.boundary === boundary) return;
        const edge = {boundary, loading: true, error: false, mention: null, retry};
        data.edges[kind] = edge;
        this._updateRows();
        try {
            const mentions = await this._request(getAdjacentDirectMessage(data.accountCode, data.scope, boundary, earlier), generation);
            const mention = mentions[0] ?? null;
            if (mention && (!mention.published || !moment.tz(mention.published, data.timezone).isValid())) {
                throw new Error('The next direct message publication date is unavailable');
            }
            edge.mention = mention;
        } catch (error) {
            if (this._current(generation) && error !== cancelled) edge.error = true;
        } finally {
            if (this._current(generation) && data.edges[kind] === edge) {
                edge.loading = false;
                this._updateRows();
            }
        }
    }

    _checkEdges(generation) {
        const data = internals.get(this);
        // These independent checks must not delay displaying or focusing the selected mention.
        // Check only exhausted ends; indexed mentions already prove that another page exists.
        if (data.first === 0) this._checkEdge('earlier', generation);
        if (data.end === data.index.length) this._checkEdge('later', generation);
    }

    _updateRows() {
        const data = internals.get(this);
        const rows = data.index.slice(data.first, data.end).map(m => {
            const mention = data.mentionsById.get(m.id);
            return {id: m.id, kind: mention ? 'mention' : 'unavailable', mention, depth: 0, parentId: null};
        });
        const earlier = this._edgeRow('earlier', data.first);
        const later = this._edgeRow('later', data.index.length - data.end);
        if (earlier) rows.unshift(earlier);
        if (later) rows.push(later);
        this.state.rows = rows;
    }

    async fetchData() {
        const data = internals.get(this);
        if (data.disposed) return this.visibleRows();
        for (const request of data.requests) request.cancel?.();
        const generation = ++data.generation;
        data.action = null;
        data.mentionsById = new Map();
        data.index = [];
        data.scope = null;
        data.edges = {earlier: null, later: null};
        this.state.loading = true;
        this.state.error = null;
        this.state.filter = '';
        this.state.dateRange = null;
        this.state.rows = [];
        try {
            const mentions = await this._request(getSelectedDirectMessage(data.accountCode, data.selectedMention.id), generation);
            const selected = mentions.find(m => m.id === data.selectedMention.id && isDirectMessage(m));
            if (!selected) throw new Error('The selected direct message is unavailable');
            const scope = directMessageScope(selected, data.timezone);
            data.mentionsById.set(selected.id, selected);
            const filter = directMessageFilter(scope);
            const index = await this._index(filter, generation);
            const focus = index.findIndex(m => m.id === selected.id);
            const first = Math.max(0, Math.min(focus - Math.floor(PAGE_SIZE / 2), index.length - PAGE_SIZE));
            const end = Math.min(index.length, first + PAGE_SIZE);
            await this._loadMentions(index, first, end, filter, generation);
            if (!this._current(generation)) return this.visibleRows();
            Object.assign(data, {scope, index, first, end});
            this.state.filter = filter;
            this.state.dateRange = {start: scope.start, end: scope.end};
            this._updateRows();
            this._checkEdges(generation);
        } catch (error) {
            if (this._current(generation) && error !== cancelled) this.state.error = error;
        } finally {
            if (this._current(generation)) this.state.loading = false;
        }
        return this.visibleRows();
    }

    async expand(row) {
        const data = internals.get(this);
        if (data.disposed || this.loading || !data.scope || !['earlier', 'later'].includes(row.kind)) return;
        if (data.action) return data.action;
        const generation = data.generation;
        const action = this._expand(row.kind === 'earlier', generation);
        data.action = action;
        try { await action; } catch (error) {
            if (this._current(generation) && error !== cancelled) throw error;
        } finally {
            if (data.action === action) data.action = null;
        }
        return this.visibleRows();
    }

    async _expand(earlier, generation) {
        const data = internals.get(this);
        let {index, first, end} = data;
        let dateRange = this.dateRange;
        let filter = this.filter;
        if ((earlier && first === 0) || (!earlier && end === index.length)) {
            const kind = earlier ? 'earlier' : 'later';
            const edge = earlier ? 'start' : 'end';
            const check = data.edges[kind];
            if (!check || check.boundary !== dateRange[edge] || check.loading) return;
            if (check.error) return this._checkEdge(kind, generation, true);
            if (!check.mention) return;
            const step = moment.tz(dateRange[edge], data.timezone).add(earlier ? -DM_DATE_RANGE_DAYS : DM_DATE_RANGE_DAYS, 'days');
            const next = moment.tz(check.mention.published, data.timezone).startOf('second');
            // Skip empty date ranges. The exclusive upper boundary must include the next mention,
            // including one published at the old boundary or with fractional seconds.
            dateRange = {...dateRange, [edge]: (earlier ? moment.min(step, next) : moment.max(step, next.add(1, 'second'))).format()};
            filter = directMessageFilter(data.scope, dateRange.start, dateRange.end);
            const previous = index;
            index = await this._index(filter, generation);
            // Preserve the displayed interval even when a refreshed index omits an unavailable mention.
            const merged = new Map(index.map(m => [m.id, m]));
            previous.slice(first, end).forEach(m => merged.set(m.id, m));
            if (merged.size > CONVERSATION_MENTION_LIMIT) this._tooLarge();
            index = [...merged.values()].sort((a, b) => compareMentionsByPublishedDate(a, b) || a.id.localeCompare(b.id));
            first = index.findIndex(m => m.id === previous[first].id);
            end = index.findIndex(m => m.id === previous[end - 1].id) + 1;
        }
        const nextFirst = earlier ? Math.max(0, first - PAGE_SIZE) : first;
        const nextEnd = earlier ? end : Math.min(index.length, end + PAGE_SIZE);
        // Cached mentions are skipped; new arrivals anywhere in the displayed interval also need mentions.
        await this._loadMentions(index, nextFirst, nextEnd, filter, generation);
        if (!this._current(generation)) return;
        Object.assign(data, {index, first: nextFirst, end: nextEnd});
        this.state.dateRange = dateRange;
        this.state.filter = filter;
        this._updateRows();
        this._checkEdges(generation);
    }

    dispose() {
        const data = internals.get(this);
        data.disposed = true;
        data.generation++;
        for (const request of data.requests) request.cancel?.();
        data.requests.clear();
        this.state.loading = false;
    }
}
